import {Box, Grid, IconButton, Typography} from '@mui/material';
import React, {useCallback, useEffect, useRef, useState} from 'react';
import AurButton from '../../common/aur-button/AurButton';
import {IconNames} from '../../common/aur-icon/AurIcon';
import EcaCustodians from './ecaAllDocuments/EcaCustodians';
import EcaProcessingExceptions from './ecaAllDocuments/EcaProcessingExceptions';
import EcaDocTypes from './ecaAllDocuments/EcaDocTypes';
import EcaFamilyData from './ecaAllDocuments/EcaFamilyData';
import AurTabWithIcons, {
  ITabIconLabel,
} from '../../common/aur-tab-with-icons/AurTabWithIcons';
import SavedSearchesTab, {SavedSearchDetails} from './SavedSearchesTab';
import {t} from 'i18next';
import LoadsTab from './LoadsTab';
import EcaExports from './ecaAllDocuments/EcaExports';
import {formatNumberWithComma} from '../../../utils/utils';
import {ReactComponent as ArrowWithBg} from '../../../assests/svg/arrow-with-bg.svg';
import {LoadFileData} from '../../client-details/project-data-table/types';
import SavedSearchRightPanel from './tabs-right-panel/SavedSearchRightPanel';
import ECATagTab from '../../eca/ECATagTab';
import {ExtendedTreeItemProps} from '../../eca-doc-list/eca-save-search/EcaSaveSearch';
import {TreeViewBaseItem} from '@mui/x-tree-view';
import TagRightPanel from './tabs-right-panel/TagRightPanel';
import {useNavigate} from 'react-router-dom';
import {TrackItemsType} from '../../eca-doc-list/eca-save-search/AurLocationTreeView';
import {SOURCE_TYPE} from '../../../constants/constants';
import {
  GA_CLICK_TYPES,
  GA_EVENT_NAMES,
  GA_REGIONS,
  GA_SUBPAGE_TYPE,
} from '../../../constants/analyticsConstants';
import {useAnalytics} from '../../../hooks/useAnalytics';
import {
  PAGE_NAME,
  usePermissionsByPage,
} from '../../../hooks/permissions/usePermissionsByPage';

interface EcaBottomSectionProps {
  ecaSummaryData: LoadFileData | null;
  onClickDocsNotTaggedSection?: (activeLoadFile?: LoadFileData) => void;
  resetTagFlag?: boolean;
  onResetTagHandled?: () => void;
}

const EcaBottomSection: React.FC<EcaBottomSectionProps> = ({
  ecaSummaryData,
  onClickDocsNotTaggedSection,
  resetTagFlag,
  onResetTagHandled,
}) => {
  useEffect(() => {
    if (resetTagFlag) {
      setSelectedTag(null);
      onResetTagHandled?.();
    }
  }, [resetTagFlag, onResetTagHandled]);
  const [activeLoadFile, setActiveLoadFile] = useState<LoadFileData | null>(
    null
  );
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [breadcrumbItems, setBreadCrumbItems] = useState<TrackItemsType[]>([]);
  const [selectedData, setSelectedData] = React.useState<{
    node: string | null;
    details: SavedSearchDetails;
  }>({node: null, details: {} as SavedSearchDetails});
  const navigate = useNavigate();
  const {trackGAEvent} = useAnalytics();

  const [selectedTag, setSelectedTag] =
    useState<TreeViewBaseItem<ExtendedTreeItemProps> | null>();

  const [showSavedSearchLoader, setShowSavedSearchLoader] = useState(false);

  const rightPanelRef = useRef<HTMLDivElement>(null);

  const {
    hasSearchesPerm,
    hasSavedSearchesPerm,
    hasExportPerm,
    hasTaggingDocumentPerm,
  } = usePermissionsByPage(PAGE_NAME.ECA_OVERVIEW);

  const tabIconLabelArray: ITabIconLabel[] = [
    {
      tabIndex: 0,
      label: t('ecaHomepageModals.tabsLeftPanel.tabLabels.loads'),
      icon: IconNames.Loads,
    },
    ...(hasSavedSearchesPerm
      ? [
          {
            tabIndex: 1,
            label: t('ecaHomepageModals.tabsLeftPanel.tabLabels.savedSearches'),
            icon: IconNames.BookmarksOutlined,
          },
        ]
      : []),
    {
      tabIndex: 2,
      label: t('ecaHomepageModals.tabsLeftPanel.tabLabels.tags'),
      icon: IconNames.Tag,
    },
  ];

  const handleTabSelection = (index: number) => {
    const tabIndex = tabIconLabelArray[index].tabIndex;
    setLoading(true);
    setSelectedData({node: null, details: {} as SavedSearchDetails});
    setSelectedTag(null);
    setBreadCrumbItems([]);
    setSelectedTabIndex(tabIndex);
    trackGAEvent({
      eventName: GA_EVENT_NAMES.CLICK,
      click: {
        name: (tabIconLabelArray[index] as any)?.label,
        type: GA_CLICK_TYPES.ICON,
        region: GA_REGIONS.WIDGET_LEFT_NAV,
        action: t('gaTracking.global.opendSubpageLabel', {
          label: (tabIconLabelArray[index] as any)?.label,
        }),
      },
    });
    trackGAEvent({
      eventName: GA_EVENT_NAMES.SUB_PAGE_VIEW,
      subPage: {
        subpage_name: (tabIconLabelArray[index] as any)?.label,
        subpage_type: GA_SUBPAGE_TYPE.TAB,
      },
    });
  };

  const handleSelectLoadFile = (loadFile: any) => {
    setActiveLoadFile(loadFile);
  };

  useEffect(() => {
    setLoading(false);
  }, [selectedTabIndex]);

  const handleSelectedSavedSearchItem = (
    node: string,
    details: SavedSearchDetails
  ) => {
    setSelectedData({node, details});
  };

  const onTagSelected = useCallback((tagInfo?: ExtendedTreeItemProps) => {
    setSelectedTag(tagInfo);
  }, []);

  const onTagFolderSelected = useCallback((tagInfo?: ExtendedTreeItemProps) => {
    setSelectedTag(tagInfo);
  }, []);
  const handleTrackItems = useCallback((items: TrackItemsType[]) => {
    setBreadCrumbItems(items);
  }, []);

  useEffect(() => {
    const panel = rightPanelRef.current;
    if (!panel) return;
    const updateHeight = () => {
      const height = panel.offsetHeight;
      document.documentElement.style.setProperty(
        '--right-panel-height',
        `${height}px`
      );
    };
    // Initial set
    updateHeight();
    // Dynamic updates
    const resizeObserver = new ResizeObserver(() => {
      updateHeight();
    });
    resizeObserver.observe(panel);
    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const renderTabContent = () => {
    switch (selectedTabIndex) {
      case 0:
        return (
          <LoadsTab
            activeLoadFile={activeLoadFile}
            onSelectLoadFile={handleSelectLoadFile}
            ecaSummaryData={ecaSummaryData}
            renderOnPage="eca-home"
          />
        );
      case 1:
        return (
          <SavedSearchesTab
            trackItems={handleTrackItems}
            selectedSavedSearchItem={handleSelectedSavedSearchItem}
            onSavedSearchSelection={(isLoading) =>
              setShowSavedSearchLoader(isLoading)
            }
          />
        );
      case 2:
        return (
          <ECATagTab
            isShowSearch={true}
            isShowFlyout={false}
            onTagSelected={onTagSelected}
            onTagFolderSelected={onTagFolderSelected}
          />
        );
      default:
        return <div>{t('ecaHomepageModals.ecaBottom.noTabSelected')}</div>;
    }
  };

  const renderDocTiles = () => {
    const data =
      selectedTabIndex === 0 && activeLoadFile === null
        ? ecaSummaryData
        : activeLoadFile;

    return (
      <>
        <div
          className={`tile ${activeLoadFile?.type === 'ALL_DOCUMENT' ? '' : 'active-all-docs'}`}
        >
          <IconButton
            className="tile-item"
            role="button"
            data-testid="kpi-btn-1"
            onClick={() => {
              if (activeLoadFile) {
                trackGAEvent({
                  eventName: GA_EVENT_NAMES.CLICK,
                  click: {
                    name: t('gaTracking.ecaHomePage.docWithinLoad'),
                    region: GA_REGIONS.WIDGET_BODY,
                    type: GA_CLICK_TYPES.TILE,
                    action: t('gaTracking.ecaHomePage.searchDocuments'),
                  },
                });
                navigate('/index-search', {
                  state: {
                    source: SOURCE_TYPE.load,
                    selected: activeLoadFile,
                  },
                });
              }
            }}
          >
            <div className="count-head">
              <Typography className="count typo-text headline2_size">
                {formatNumberWithComma(data?.totalDocumentsCount ?? 0)}
              </Typography>
              <ArrowWithBg aria-hidden="true" />
            </div>
            <Typography className="description typo-text subhead4_size">
              {t('ecaHomepageModals.ecaBottom.docWithinLoan')}
            </Typography>
          </IconButton>
          <div className="tile-item"></div>
        </div>
        {activeLoadFile?.type === 'ALL_DOCUMENT' ? (
          <>
            <div className="tile-divider"></div>
            {hasTaggingDocumentPerm && (
              <div className="tile">
                <IconButton
                  className="tile-item"
                  role="button"
                  data-testid="kpi-btn-2"
                  onClick={() => {
                    onClickDocsNotTaggedSection?.(activeLoadFile);
                    trackGAEvent({
                      eventName: GA_EVENT_NAMES.SEARCH,
                      click: {
                        name: t('gaTracking.ecaHomePage.docNotTagged'),
                        region: GA_REGIONS.WIDGET_BODY,
                        type: GA_CLICK_TYPES.TILE,
                        action: t('gaTracking.ecaHomePage.searchDocuments'),
                      },
                    });
                  }}
                >
                  <div className="count-head">
                    <Typography className="count typo-text headline2_size">
                      {formatNumberWithComma(data?.unTaggedDocumentCount ?? 0)}
                    </Typography>
                    <ArrowWithBg aria-hidden="true" />
                  </div>
                  <Typography className="description typo-text subhead4_size">
                    {t('ecaHomepageModals.ecaBottom.docNotTagged')}
                  </Typography>
                </IconButton>
                <div className="tile-item"></div>
              </div>
            )}
            <div className="tile-divider"></div>
            {hasExportPerm && (
              <div className="tile">
                <IconButton
                  className="tile-item"
                  data-testid="kpi-btn-3"
                  role="button"
                  onClick={() => {
                    if (activeLoadFile) {
                      trackGAEvent({
                        eventName: GA_EVENT_NAMES.SEARCH,
                        click: {
                          name: t('gaTracking.ecaHomePage.notExported'),
                          region: GA_REGIONS.WIDGET_BODY,
                          type: GA_CLICK_TYPES.TILE,
                          action: t('gaTracking.ecaHomePage.searchDocuments'),
                        },
                      });
                      navigate('/index-search', {
                        state: {
                          source: SOURCE_TYPE.notExported,
                          selected: activeLoadFile,
                        },
                      });
                    }
                  }}
                >
                  <div className="count-head">
                    <Typography className="count typo-text headline2_size">
                      {formatNumberWithComma(
                        data?.unExportedDocumentCount ?? 0
                      )}
                    </Typography>
                    <ArrowWithBg aria-hidden="true" />
                  </div>
                  <Typography className="description typo-text subhead4_size">
                    {t('ecaHomepageModals.ecaBottom.notExported')}
                  </Typography>
                </IconButton>
              </div>
            )}
            {hasExportPerm && (
              <>
                <div className="tile-divider" />
                <div className="tile">
                  <IconButton
                    className="tile-item"
                    data-testid="kpi-btn-3"
                    role="button"
                    onClick={() => {
                      if (activeLoadFile) {
                        trackGAEvent({
                          eventName: GA_EVENT_NAMES.SEARCH,
                          click: {
                            name: t('gaTracking.ecaHomePage.exported'),
                            region: GA_REGIONS.WIDGET_BODY,
                            type: GA_CLICK_TYPES.TILE,
                            action: t('gaTracking.ecaHomePage.searchDocuments'),
                          },
                        });
                        navigate('/index-search', {
                          state: {
                            source: SOURCE_TYPE.exported,
                            selected: activeLoadFile,
                          },
                        });
                      }
                    }}
                  >
                    <div className="count-head">
                      <Typography className="count typo-text headline2_size">
                        {formatNumberWithComma(
                          data?.exportedDocumentCount ?? 0
                        )}
                      </Typography>
                      <ArrowWithBg aria-hidden="true" />
                    </div>
                    <Typography className="description typo-text subhead4_size">
                      {t('ecaHomepageModals.ecaBottom.exported')}
                    </Typography>
                  </IconButton>
                </div>
              </>
            )}
          </>
        ) : (
          <></>
        )}
      </>
    );
  };

  return (
    <>
      <Box>
        <Typography className="typo-text headline1_size header-spacing">
          {t('ecaHomepageModals.ecaBottom.letTakeDeeperDive')}
        </Typography>
      </Box>
      <Grid container spacing={0}>
        <Grid item md={2.5} className="bg-custom-color">
          <div className="eca-doc-tabs">
            <AurTabWithIcons
              iconLabelArray={tabIconLabelArray}
              defaultTabIndex={selectedTabIndex}
              onSelectTabIndex={handleTabSelection}
            />
          </div>
          <div className="tabs-area-doc">
            {loading ? (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="100%"
              >
                {t('ecaHomepageModals.ecaBottom.loading')}
              </Box>
            ) : (
              renderTabContent()
            )}
          </div>
        </Grid>
        <Grid item md={9.5}>
          <div ref={rightPanelRef} className="right-panel-wrapper">
            {selectedTabIndex === 0 && (
              <>
                <div className="search-header">
                  <Box display={'flex'} justifyContent={'space-between'}>
                    <div className="header-all-documents">
                      <Typography className="typo-text subhead4_size">
                        {t('ecaHomepageModals.ecaBottom.showingLatestActivity')}
                      </Typography>
                      <Typography className="typo-text subhead4_size">
                        {activeLoadFile && activeLoadFile.name
                          ? `${activeLoadFile.name}`
                          : `All Documents`}
                      </Typography>
                    </div>
                    {hasSearchesPerm && (
                      <div>
                        <AurButton
                          dataTestId="search-button"
                          onClick={() => {
                            if (activeLoadFile) {
                              trackGAEvent({
                                eventName: GA_EVENT_NAMES.SEARCH,
                                click: {
                                  name: t(
                                    'gaTracking.ecaHomePage.searchThisLoad'
                                  ),
                                  region: GA_REGIONS.WIDGET_BODY,
                                  type: GA_CLICK_TYPES.BUTTON,
                                  action: t(
                                    'gaTracking.ecaHomePage.searchDocuments'
                                  ),
                                },
                                subPage: {
                                  subpage_name: (
                                    tabIconLabelArray[selectedTabIndex] as any
                                  )?.label,
                                  subpage_type: GA_SUBPAGE_TYPE.TAB,
                                },
                              });
                              navigate('/index-search', {
                                state: {
                                  source: SOURCE_TYPE.load,
                                  selected: activeLoadFile,
                                },
                              });
                            }
                          }}
                          type="continue"
                          size="Medium"
                          title={t(
                            'ecaHomepageModals.ecaBottom.searchThisLoad'
                          )}
                          ariaLabel={t(
                            'ecaHomepageModals.ecaBottom.searchThisLoad'
                          )}
                          label={t(
                            'ecaHomepageModals.ecaBottom.searchThisLoad'
                          )}
                          classNames="typo-text buttonMedium_size"
                        />
                      </div>
                    )}
                  </Box>
                </div>

                <div className="eca-bottom-area">
                  {hasSearchesPerm && (
                    <Box className="eca-tiles-bottom">{renderDocTiles()}</Box>
                  )}

                  <div className="eca-doc-column">
                    <Grid container spacing={2}>
                      <EcaCustodians loadFileId={activeLoadFile?.exportId} />
                      <EcaProcessingExceptions
                        loadFileId={activeLoadFile?.exportId}
                      />
                      <EcaDocTypes loadFileId={activeLoadFile?.exportId} />
                      <EcaFamilyData loadFileId={activeLoadFile?.exportId} />
                      {!activeLoadFile?.exportId && <EcaExports />}
                    </Grid>
                  </div>
                </div>
              </>
            )}
            {selectedTabIndex === 1 && (
              <SavedSearchRightPanel
                node={selectedData?.node}
                details={selectedData.details}
                breadcrumbItems={breadcrumbItems}
                showSavedSearchLoader={showSavedSearchLoader}
              />
            )}
            {selectedTabIndex === 2 && (
              <TagRightPanel selectedTag={selectedTag} />
            )}
          </div>
        </Grid>
      </Grid>
    </>
  );
};

export default EcaBottomSection;
